/**
 * P&L Statement view (M27L)
 * ==========================
 * The canonical "Plan output" page. Full GAAP-shaped P&L (Revenue ->
 * Gross Profit -> OpEx -> EBITDA -> D&A -> EBIT -> Interest -> Tax ->
 * Net Income) with monthly columns + Q1..Q4 + FY rollup, Pigment-style
 * cell-level overrides (blue bar indicator, click to edit, delete to
 * restore).
 *
 * Reads GET /api/v1/planning/scenarios/{id}/statement; writes via
 * POST/DELETE /scenarios/{id}/statement/override.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_V1, authFetch } from '../../constants';

const API = `${API_V1}/planning`;

// ── Types ───────────────────────────────────────────────────────────

interface Cell {
  value: number;
  computed_value: number;
  is_overridden: boolean;
  override_value: number | null;
  source: string;
  read_only?: boolean;
}

interface StatementRow {
  key: string | null;
  label: string;
  level: number;
  kind: 'header' | 'metric' | 'subtotal' | 'assumption';
  period_handling: 'PER_PERIOD' | 'SNAPSHOT';
  cells: Record<string, Cell>;
  row_total: number | null;
}

interface StatementPayload {
  scenario_id: number;
  scenario_name: string;
  fiscal_year: number | null;
  has_computed_outputs: boolean;
  columns: string[];
  monthly_columns: string[];
  rollup_columns: string[];
  rows: StatementRow[];
  override_count: number;
}

interface Scenario {
  id: number;
  name: string;
  has_computed_outputs: boolean;
}

// ── Constants ───────────────────────────────────────────────────────

const PANEL: React.CSSProperties = {
  background: '#fff',
  border: '1px solid var(--border-subtle, #e2e8f0)',
  borderRadius: 6,
  padding: 16,
  marginBottom: 16,
};

// Pigment-style override visual: solid blue left bar on the cell
const OVERRIDE_BAR_COLOR = '#3b82f6'; // blue-500
const HEADER_LABEL_COLOR = '#64748b';
const SUBTOTAL_BG = '#f8fafc';

// ── Component ───────────────────────────────────────────────────────

interface Props {
  scenarios: Scenario[];
  toast: (msg: string, ok?: boolean) => void;
}

export default function StatementView({ scenarios, toast }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showRollup, setShowRollup] = useState(true);
  const [section, setSection] = useState<'all' | 'PL' | 'CASH'>('all');
  const [payload, setPayload] = useState<StatementPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ metric: string; period: string; current: number } | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  // Auto-pick first computed scenario, fall back to first overall
  useEffect(() => {
    if (selectedId == null && scenarios.length > 0) {
      const computed = scenarios.find(s => s.has_computed_outputs);
      setSelectedId((computed || scenarios[0]).id);
    }
  }, [scenarios, selectedId]);

  const load = useCallback(async () => {
    if (selectedId == null) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('include_rollup', String(showRollup));
      if (section !== 'all') params.set('section', section);
      const res = await authFetch(`${API}/scenarios/${selectedId}/statement?${params}`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `failed to load statement (${res.status})`);
      }
      setPayload(await res.json());
    } catch (e: any) {
      setError(String(e?.message || e));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [selectedId, showRollup, section]);

  useEffect(() => { load(); }, [load]);

  const saveOverride = useCallback(async () => {
    if (!editing || selectedId == null) return;
    const v = Number(draft);
    if (!Number.isFinite(v)) {
      toast('Override must be a number', false);
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`${API}/scenarios/${selectedId}/statement/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metric: editing.metric, period: editing.period, value: v,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast(data.detail || `override failed (${res.status})`, false);
        return;
      }
      toast(`Override saved: ${editing.metric} @ ${editing.period}`, true);
      setEditing(null);
      setDraft('');
      load();
    } finally {
      setBusy(false);
    }
  }, [editing, draft, selectedId, toast, load]);

  const restoreOverride = useCallback(async (metric: string, period: string) => {
    if (selectedId == null) return;
    setBusy(true);
    try {
      const params = new URLSearchParams({ metric, period });
      const res = await authFetch(
        `${API}/scenarios/${selectedId}/statement/override?${params}`,
        { method: 'DELETE' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast(data.detail || `restore failed (${res.status})`, false);
        return;
      }
      toast(`Restored ${metric} @ ${period}`, true);
      load();
    } finally {
      setBusy(false);
    }
  }, [selectedId, toast, load]);

  return (
    <div>
      {/* Controls bar */}
      <div style={{ ...PANEL, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>Scenario</label>
          <select
            value={selectedId ?? ''}
            onChange={e => setSelectedId(Number(e.target.value) || null)}
            style={selectStyle}
          >
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{!s.has_computed_outputs ? ' (not computed)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Section</label>
          <select value={section} onChange={e => setSection(e.target.value as any)} style={selectStyle}>
            <option value="all">Full statement</option>
            <option value="PL">P&L only</option>
            <option value="CASH">Cash & Working Capital</option>
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showRollup}
            onChange={e => setShowRollup(e.target.checked)}
          />
          Show quarterly + FY rollup
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          {payload && payload.override_count > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>
              <span style={{ display: 'inline-block', width: 3, height: 10, background: OVERRIDE_BAR_COLOR, marginRight: 4, verticalAlign: 'middle' }} />
              {payload.override_count} override{payload.override_count === 1 ? '' : 's'} active
            </span>
          )}
          <button onClick={load} disabled={loading} style={btnStyle}>
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
      </div>

      {/* Status banners */}
      {loading && !payload && (
        <div style={{ ...PANEL, color: 'var(--text-hint)' }}>Loading statement…</div>
      )}
      {error && (
        <div style={{ ...PANEL, color: '#991b1b' }}>Failed to load: {error}</div>
      )}
      {payload && !payload.has_computed_outputs && (
        <div style={{ ...PANEL, background: '#fef3c7', borderColor: '#fde68a', color: '#713f12', fontSize: 12 }}>
          This scenario hasn't been computed yet. Open Scenarios tab and click <strong>Compute</strong>
          to populate the statement — the formula graph runs across the fiscal year.
        </div>
      )}

      {/* Statement table */}
      {payload && payload.rows.length > 0 && (
        <div style={{ ...PANEL, padding: 0, overflow: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ ...headerCellStyle, textAlign: 'left', minWidth: 220, position: 'sticky', left: 0, background: '#f1f5f9', zIndex: 1 }}>
                  Statement
                </th>
                {payload.columns.map(col => {
                  const isRollup = payload.rollup_columns.includes(col);
                  return (
                    <th
                      key={col}
                      style={{
                        ...headerCellStyle,
                        background: isRollup ? '#e2e8f0' : '#f1f5f9',
                        fontWeight: isRollup ? 700 : 600,
                        textAlign: 'right',
                        minWidth: isRollup ? 90 : 80,
                      }}
                    >
                      {formatColLabel(col)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {payload.rows.map((row, ri) => (
                <RowView
                  key={`${row.key ?? 'h'}-${ri}`}
                  row={row}
                  columns={payload.columns}
                  monthlyColumns={payload.monthly_columns}
                  rollupColumns={payload.rollup_columns}
                  onEdit={(metric, period, current) => {
                    setEditing({ metric, period, current });
                    setDraft(String(current));
                  }}
                  onRestore={restoreOverride}
                  busy={busy}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Override edit modal */}
      {editing && (
        <div
          onClick={() => !busy && setEditing(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: 24, minWidth: 340,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 6 }}>
              Override cell
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-hint)', marginBottom: 12 }}>
              <strong>{editing.metric}</strong> @ <strong>{editing.period}</strong>
              {' · '}current computed: {formatNumber(editing.current)}
            </div>
            <input
              type="number"
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveOverride();
                if (e.key === 'Escape') setEditing(null);
              }}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14,
                border: '1px solid var(--border-default, #cbd5e1)', borderRadius: 4,
                fontFamily: 'ui-monospace, monospace', textAlign: 'right',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 6 }}>
              Override is non-destructive — delete it from the cell menu to restore
              the formula result.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 16 }}>
              <button onClick={() => setEditing(null)} disabled={busy} style={btnStyle}>Cancel</button>
              <button onClick={saveOverride} disabled={busy || !draft.trim()} style={{ ...btnStyle, ...primaryBtnStyle }}>
                {busy ? 'Saving…' : 'Save override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Row view ───────────────────────────────────────────────────────

function RowView({
  row, columns, monthlyColumns, rollupColumns, onEdit, onRestore, busy,
}: {
  row: StatementRow;
  columns: string[];
  monthlyColumns: string[];
  rollupColumns: string[];
  onEdit: (metric: string, period: string, current: number) => void;
  onRestore: (metric: string, period: string) => void;
  busy: boolean;
}) {
  if (row.kind === 'header') {
    return (
      <tr>
        <td
          colSpan={1 + columns.length}
          style={{
            padding: '12px 14px 6px',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            color: HEADER_LABEL_COLOR, textTransform: 'uppercase',
            borderTop: '1px solid #e2e8f0', background: '#fafafa',
          }}
        >
          {row.label}
        </td>
      </tr>
    );
  }

  const isSubtotal = row.kind === 'subtotal';
  const rowBg = isSubtotal ? SUBTOTAL_BG : 'transparent';

  return (
    <tr style={{ background: rowBg, borderTop: '1px solid #f1f5f9' }}>
      <td
        style={{
          ...rowLabelStyle,
          paddingLeft: 14 + row.level * 16,
          fontWeight: isSubtotal ? 700 : 500,
          color: isSubtotal ? 'var(--navy-900)' : '#334155',
          position: 'sticky', left: 0, background: rowBg, zIndex: 1,
        }}
      >
        {row.label}
      </td>
      {columns.map(col => {
        const cell = row.cells[col];
        if (!cell) {
          return <td key={col} style={emptyCellStyle}>—</td>;
        }
        const isRollup = rollupColumns.includes(col);
        const editable = !isRollup && !cell.read_only && row.key !== null && row.kind !== 'header';
        return (
          <td
            key={col}
            onClick={() => {
              if (!editable || busy) return;
              onEdit(row.key!, col, cell.value);
            }}
            onContextMenu={e => {
              if (!cell.is_overridden || busy) return;
              e.preventDefault();
              if (window.confirm(`Restore computed value for ${row.label} @ ${col}?`)) {
                onRestore(row.key!, col);
              }
            }}
            title={
              cell.is_overridden
                ? `Override: ${formatNumber(cell.value)}\nComputed: ${formatNumber(cell.computed_value)}\nRight-click to restore`
                : editable ? 'Click to override' : ''
            }
            style={{
              ...numericCellStyle,
              background: isRollup
                ? (isSubtotal ? '#e2e8f0' : '#f1f5f9')
                : (isSubtotal ? SUBTOTAL_BG : 'transparent'),
              fontWeight: isSubtotal || isRollup ? 700 : 400,
              cursor: editable ? 'pointer' : 'default',
              borderLeft: cell.is_overridden ? `3px solid ${OVERRIDE_BAR_COLOR}` : '3px solid transparent',
              color: cell.value < 0 ? '#b91c1c' : 'inherit',
            }}
          >
            {formatNumber(cell.value)}
          </td>
        );
      })}
    </tr>
  );
}

// ── Styles + helpers ───────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: 'var(--text-hint)', marginBottom: 4,
};
const selectStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12, border: '1px solid var(--border-default, #cbd5e1)',
  borderRadius: 4, background: '#fff',
};
const btnStyle: React.CSSProperties = {
  padding: '6px 12px', fontSize: 12, fontWeight: 600,
  border: '1px solid var(--border-default, #cbd5e1)', background: '#fff',
  borderRadius: 4, cursor: 'pointer', color: 'var(--navy-900, #1e3a5f)',
};
const primaryBtnStyle: React.CSSProperties = {
  background: 'var(--navy-900, #1e3a5f)', color: '#fff', border: 'none',
};

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12,
};
const headerCellStyle: React.CSSProperties = {
  padding: '10px 8px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-hint)', whiteSpace: 'nowrap',
  borderBottom: '1px solid #cbd5e1',
};
const rowLabelStyle: React.CSSProperties = {
  padding: '8px 14px', fontSize: 12,
};
const numericCellStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 12, textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'nowrap',
};
const emptyCellStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 12, textAlign: 'right',
  color: 'var(--text-hint)',
};

function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  const abs = Math.abs(n);
  // Compact for >= 1M, else exact with thousand separators and 0 decimals
  if (abs >= 1_000_000) {
    return `${n < 0 ? '-' : ''}${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatColLabel(col: string): string {
  // '2026-01' -> 'Jan 26'
  // '2026-Q1' -> 'Q1'
  // '2026' -> 'FY26'
  if (/^\d{4}-Q[1-4]$/.test(col)) return col.split('-')[1];
  if (/^\d{4}$/.test(col)) return `FY${col.slice(2)}`;
  if (/^\d{4}-\d{2}$/.test(col)) {
    const [y, m] = col.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
  }
  return col;
}
